<template>
    <div class="tw:flex tw:flex-col tw:gap-5 tw:pb-4">

        <!-- ─── Comparativa: butaca/plaza actual vs nueva ────────────────── -->
        <div class="tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:overflow-hidden">
            <div class="tw:px-4 tw:py-3 tw:bg-gray-50 tw:border-b tw:border-gray-100">
                <h3 class="tw:text-base tw:font-bold tw:text-gray-800 tw:m-0">
                    {{ $t('perfil.cambio_asiento.resumen_titulo_butaca', $t('perfil.cambio_asiento.resumen_titulo')) }}
                </h3>
            </div>

            <div class="tw:px-4 tw:py-3 tw:flex tw:flex-col tw:md:flex-row md:tw:items-center tw:gap-4">

                <!-- Butaca/Plaza ACTUAL -->
                <template v-if="asientoActual">
                    <div class="tw:flex-1 tw:flex tw:flex-col tw:items-center tw:gap-2 tw:p-4 tw:rounded-lg tw:bg-gray-50 tw:text-center">
                        <div class="tw:w-10 tw:h-10 tw:rounded-full tw:bg-gray-100 tw:flex tw:items-center tw:justify-center">
                            <i class="material-icons tw:text-[22px] tw:text-gray-400">{{ esPlaza ? 'airline_seat_recline_normal' : 'event_seat' }}</i>
                        </div>
                        <div>
                            <p class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-gray-400 tw:mb-1">
                                {{ $t('perfil.cambio_asiento.butaca_actual') }}
                            </p>
                            <p v-if="asientoActual.zona" class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:mb-0">{{ asientoActual.zona }}</p>
                            <p v-if="!esPlaza" class="tw:text-sm tw:text-gray-500 tw:mb-0">
                                {{ $t('perfil.cambio_asiento.fila') }} {{ asientoActual.fila }}
                                — {{ $t('perfil.cambio_asiento.asiento') }} {{ asientoActual.numero }}
                            </p>
                            <p v-else class="tw:text-sm tw:text-gray-500 tw:mb-0">
                                {{ $t('perfil.cambio_asiento.plaza_label', { plaza_id: asientoActual.numero || '—' }) }}
                            </p>
                            <p class="tw:text-xs tw:text-gray-400 tw:mt-1">
                                {{ $t('perfil.cambio_asiento.tipo_abono_actual') }}: {{ asientoActual.tipoAbono }}
                            </p>
                        </div>
                    </div>

                    <!-- Flecha: mobile ↓, desktop → -->
                    <div class="tw:flex tw:md:hidden tw:justify-center">
                        <i class="material-icons tw:text-gray-300 tw:text-lg">arrow_downward</i>
                    </div>
                    <div class="tw:hidden tw:md:flex tw:md:items-center">
                        <i class="material-icons tw:text-gray-300 tw:text-lg">arrow_forward</i>
                    </div>
                </template>

                <!-- Butaca/Plaza NUEVA -->
                <div class="tw:flex-1 tw:flex tw:flex-col tw:items-center tw:gap-2 tw:p-4 tw:rounded-lg tw:bg-green-50 tw:text-center">
                    <div class="tw:w-10 tw:h-10 tw:rounded-full tw:bg-green-50 tw:flex tw:items-center tw:justify-center">
                        <i class="material-icons tw:text-[22px] tw:text-green-500">{{ esPlaza ? 'airline_seat_recline_normal' : 'event_seat' }}</i>
                    </div>
                    <div>
                        <p class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-green-600 tw:mb-1">
                            {{ $t('perfil.cambio_asiento.butaca_nueva') }}
                        </p>
                        <p v-if="zonaNueva" class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:mb-0">
                            <!--
                                Nombre de la zona/región. El wrapper legacy
                                (`SeleccionarAsientosRegionAnexa`) emite la zona
                                cruda del API; el nombre puede venir como
                                `nombre` (es), `name` (legacy canónico) o
                                `descripcion`. Aceptamos todos. Si no hay
                                región no se renderiza la línea (ni espacio).
                            -->
                            {{ zonaNueva }}
                        </p>
                        <p v-if="asientoSeleccionado && !esPlaza" class="tw:text-sm tw:text-gray-500 tw:mb-0">
                            <!--
                                Butaca nueva. El campo puede venir normalizado
                                (row/number desde useCambioAsientoAsientos.toggleAsiento)
                                o crudo del wrapper legacy (fila/butaca desde
                                SeleccionarAsientosRegionAnexa). Aceptamos ambos.
                            -->
                            {{ $t('perfil.cambio_asiento.fila') }} {{ asientoSeleccionado.row ?? asientoSeleccionado.fila }}
                            — {{ $t('perfil.cambio_asiento.asiento') }} {{ asientoSeleccionado.number ?? asientoSeleccionado.butaca }}
                        </p>
                        <p v-else-if="asientoSeleccionado && esPlaza" class="tw:text-sm tw:text-gray-500 tw:mb-0">
                            <!--
                                Plaza nueva. Mostramos el número de plaza con el
                                mismo formato que el asiento actual (`numero`).
                                Aceptamos tanto la data normalizada (`number`,
                                `row`/`fila`) como la cruda del wrapper o servicio
                                (`asiento`, `butaca`). Si tiene fila+butaca
                                (plazas numeradas) mostramos "Fila X — Butaca Y".
                                Si no, mostramos el número de plaza.
                            -->
                            <template v-if="(asientoSeleccionado.fila && asientoSeleccionado.butaca)">
                                {{ $t('perfil.cambio_asiento.fila') }} {{ asientoSeleccionado.fila }}
                                — {{ $t('perfil.cambio_asiento.butaca') }} {{ asientoSeleccionado.butaca }}
                            </template>
                            <template v-else>
                                {{ $t('perfil.cambio_asiento.plaza_label', { plaza_id: asientoSeleccionado.number ?? asientoSeleccionado.asiento ?? asientoSeleccionado.numero ?? '—' }) }}
                            </template>
                            <span v-if="asientoSeleccionado.sector_name" class="tw:text-xs tw:text-gray-400">
                                — {{ asientoSeleccionado.sector_name }}
                            </span>
                        </p>
                        <p v-else class="tw:text-sm tw:text-gray-500 tw:mb-0">—</p>
                        <p v-if="tipoAbonoNuevoDescripcion" class="tw:text-xs tw:text-gray-400 tw:mt-1">
                            {{ $t('perfil.cambio_asiento.tipo_abono_nuevo') }}: {{ tipoAbonoNuevoDescripcion }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Importe del cambio (oculto si es gratuito: ya lo muestra el bloque Total) -->
            <div v-if="precioTotal > 0" class="tw:px-4 tw:py-2 tw:border-t tw:border-gray-100 tw:flex tw:items-center tw:justify-between">
                <span class="tw:text-sm tw:text-gray-700">{{ $t('perfil.cambio_asiento.importe_cambio') }}</span>
                <span class="tw:text-sm tw:font-semibold tw:text-gray-900">{{ formatearPrecio(importeCambio) }}</span>
            </div>
            <!-- Gastos de gestión (solo si > 0 y NO es gratuito) -->
            <div v-if="mostrarGastosGestion && precioTotal > 0" class="tw:px-4 tw:py-2 tw:border-t tw:border-gray-100 tw:flex tw:items-center tw:justify-between">
                <span class="tw:text-sm tw:text-gray-700">{{ $t('perfil.cambio_asiento.gastos_gestion') }}</span>
                <span class="tw:text-sm tw:font-semibold tw:text-gray-900">{{ formatearPrecio(priceManagement) }}</span>
            </div>
            <!-- Separador -->
            <div class="tw:border-t tw:border-dashed tw:border-gray-300"></div>

            <!-- Total -->
            <div class="tw:px-4 tw:py-3 tw:flex tw:items-center tw:justify-between tw:bg-gray-50">
                <span class="tw:text-sm tw:font-bold tw:text-gray-800">{{ $t('perfil.cambio_asiento.total') }}</span>
                <span class="tw:text-base tw:font-extrabold tw:text-gray-900" :class="{ 'tw:text-green-600': precioTotal === 0 }">
                    {{ precioTotal === 0 ? $t('perfil.cambio_asiento.gratuito') : formatearPrecio(precioTotal) }}
                </span>
            </div>
        </div>

        <!-- ─── TPV Redsys (formulario): form oculto + aviso de redirección ── -->
        <!--
            Cuando `reservar()` devuelve `formulario` (HTML con un form de TPV
            Redsys) en lugar de `jet_id`, el pago NO tiene paso propio: se
            submitea directamente desde aquí al pulsar "Continuar al pago"
            (mismo patrón que la venta online: ResumenCompraComponent /
            PasoSeisComponent). El div se oculta con v-show para no romper
            el layout; el form vive dentro de él en el DOM real.
        -->
        <template v-if="formulario">
            <div
                id="formulario-compra"
                v-html="formulario"
                v-show="false"
            ></div>
            <div class="tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:p-5">
                <p class="tw:text-sm tw:text-gray-600 tw:m-0 tw:mb-4 tw:text-center">
                    {{ $t('perfil.cambio_asiento.redsys_redirect_info', 'Al confirmar serás redirigido a la pasarela de pago segura del banco.') }}
                </p>
            </div>
        </template>

        <!-- ─── Error: pago requerido pero el backend no devolvió pasarela ── -->
        <div
            v-if="precioTotal > 0 && !jetId && !formulario"
            data-testid="cambio-butaca-error-pago"
            class="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:flex tw:items-start tw:gap-2"
        >
            <i class="material-icons tw:text-red-500 tw:text-lg">error_outline</i>
            <p class="tw:text-sm tw:text-red-700 tw:m-0">
                {{ $t('perfil.cambio_asiento.error_pago_no_disponible', 'No se pudo iniciar el pago. Vuelve atrás e inténtalo de nuevo.') }}
            </p>
        </div>

        <!-- ─── Error: TPV Redsys no iniciado (HTML sin <form> en el DOM) ─── -->
        <div
            v-if="errorPagoRedsys"
            data-testid="cambio-butaca-error-redsys"
            class="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:flex tw:items-start tw:gap-2"
        >
            <i class="material-icons tw:text-red-500 tw:text-lg">error_outline</i>
            <p class="tw:text-sm tw:text-red-700 tw:m-0">
                {{ $t('perfil.cambio_asiento.redsys_error_form_missing', 'No se pudo iniciar el pago en la pasarela. Vuelve a intentarlo.') }}
            </p>
        </div>

        <!-- ─── Botones de acción ──────────────────────────────────────────── -->
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
            <!-- Volver -->
            <Button
                variant="alt"
                :disabled="enviandoRedsys"
                @click="$emit('back')"
            >
                <span class="tw:flex tw:items-center tw:gap-1">
                    <i class="material-icons tw:text-base">arrow_back</i>
                    {{ $t('perfil.cambio_asiento.atras') }}
                </span>
            </Button>

            <!-- Gratuito: Confirmar directamente -->
            <Button
                v-if="precioTotal === 0"
                variant="alt"
                :disabled="loading"
                :loading="loading"
                @click="$emit('confirmar')"
            >
                {{ $t('perfil.cambio_asiento.confirmar_cambio') }}
            </Button>

            <!-- Pago requerido — Paycomet (jetId): avanzar al paso de pago -->
            <Button
                v-else-if="jetId"
                data-testid="cambio-butaca-btn-continuar-pago"
                variant="alt"
                :disabled="loading"
                :loading="loading"
                @click="$emit('continuar-pago')"
            >
                {{ $t('perfil.cambio_asiento.continuar_pago', 'Continuar al pago') }}
            </Button>

            <!-- Pago requerido — TPV Redsys (formulario): submit local del form -->
            <Button
                v-else-if="formulario"
                data-testid="cambio-butaca-btn-continuar-pago-redsys"
                variant="alt"
                :disabled="enviandoRedsys"
                :loading="enviandoRedsys"
                @click="confirmarPagoRedsys"
            >
                {{ $t('perfil.cambio_asiento.continuar_pago', 'Continuar al pago') }}
            </Button>

        <!-- Pago requerido sin jetId ni formulario (no debería ocurrir): no se
             muestra botón de pago; el banner de error de arriba avisa al usuario
             y el botón Volver permite reintentar el flujo. -->
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, nextTick, onDeactivated, onUnmounted } from 'vue';
    import Button from '@/modules/front/ui/Button.vue';
    import { marcarPagoEnCurso } from '@/utils/pagoEnCurso';

    const props = defineProps({
        /** Zona seleccionada en el paso anterior */
        zonaSeleccionada: {
            type: Object,
            default: null,
        },
        /** Butaca única seleccionada en el paso anterior */
        asientoSeleccionado: {
            type: Object,
            default: null,
        },
        /** Importe del cambio (precioTotal íntegro, puede ser 0) */
        importe: {
            type: Number,
            default: 0,
        },
        /** Importe base del cambio (sin gastos de gestión) */
        importeCambio: {
            type: Number,
            default: 0,
        },
        /** Gastos de gestión del cambio */
        priceManagement: {
            type: Number,
            default: 0,
        },
        /** Si mostrar o no los gastos de gestión (priceManagement > 0) */
        mostrarGastosGestion: {
            type: Boolean,
            default: false,
        },
        /** Precio total a pagar (0 = gratuito) */
        precioTotal: {
            type: Number,
            default: 0,
        },
        /** Configuración interna de gestión de precios (del backend) */
        priceManagementInterno: {
            type: [Object, String, Boolean],
            default: null,
        },
        /** Loading general (reserva o confirmación en curso) */
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Detalle completo del abono (para mostrar la butaca actual).
         * Incluye asiento.region.nombre, asiento.row/fila, asiento.seat/asiento.
         */
        abonoDetail: {
            type: Object,
            default: null,
        },
        /** Descripción del tipo de abono seleccionado para la nueva butaca */
        tipoAbonoNuevoDescripcion: {
            type: String,
            default: null,
        },
        /**
         * Jet ID devuelto por `reservar()` cuando el TPV es Paycomet.
         * Cuando está presente y `precioTotal > 0`, el botón de acción
         * avanza al paso de pago (PASO_PAGO).
         */
        jetId: {
            type: [String, Number],
            default: null,
        },
        /**
         * HTML del formulario de TPV Redsys devuelto por `reservar()`.
         * Cuando está presente (y `jetId` es null), el botón de acción
         * `continuar_pago` ejecuta `confirmarPagoRedsys()` y auto-submitea
         * el form desde aquí — NO avanza a un paso de pago (que solo existe
         * con Paycomet).
         */
        formulario: {
            type: String,
            default: null,
        },
        /**
         * ID de sesión de reserva. Se usa para marcar el pago en curso
         * antes de submiterar el form de TPV Redsys: la navegación a la
         * pasarela desmonta la SPA y la liberación de sesión no debe
         * borrar la reserva hasta que el webhook procese el pago.
         */
        sessionId: {
            type: String,
            default: null,
        },
    });

    const emit = defineEmits([
        /** Retroceder al paso de butacas */
        'back',
        /** Gratuito: confirmar cambio */
        'confirmar',
        /** Pago requerido (Paycomet): avanzar al paso de pago (traducido a nextStep por el padre) */
        'continuar-pago',
    ]);

    /** `true` mientras se envía el formulario de TPV Redsys (evita doble submit). */
    const enviandoRedsys = ref(false);

    /**
     * `true` cuando el click en "Continuar al pago" no pudo iniciar el TPV
     * Redsys porque el HTML devuelto por el backend no contiene `<form>`
     * (no hay nada que auto-submitear). Muestra un banner de error y deja
     * el botón operativo para reintentar.
     */
    const errorPagoRedsys = ref(false);

    /** Handle del setTimeout de auto-submit (para cancelarlo si el paso se desactiva). */
    let redsysTimer = null;

    /**
     * Cancela un envío Redsys pendiente y resetea el flag.
     *
     * Se invoca al desactivarse el paso (`keep-alive`: el DOM se desmonta y un
     * submit diferido jamás llegaría) y al destruirse el componente. Con esto
     * la instancia cacheada por `keep-alive` nunca queda atascada con
     * `enviandoRedsys = true` si el usuario retrocede dentro de la ventana de
     * 500ms del auto-submit.
     */
    function cancelarEnvioRedsys() {
        if (redsysTimer !== null) {
            clearTimeout(redsysTimer);
            redsysTimer = null;
        }
        enviandoRedsys.value = false;
        // Resetear también el banner de error Redsys. Tras un intento fallido
        // (HTML sin <form>), la instancia cacheada por `keep-alive` mantiene
        // `errorPagoRedsys=true` hasta el próximo click; al volver y re-entrar
        // al paso el banner aparece stale. Mismo patrón de reset que
        // `confirmarPagoRedsys` (L374).
        errorPagoRedsys.value = false;
    }

    /**
     * Envía el formulario de TPV Redsys directamente desde la confirmación.
     * Renderiza (o re-renderiza) el div oculto `#formulario-compra` con el
     * HTML del backend y, tras el próximo tick + 500ms, busca
     * `#formulario-compra form` y lo submitea.
     *
     * Sigue el patrón de la venta online de abonos (`ResumenCompraComponent`)
     * y de bases (`PasoSeisComponent.cargarFormularioPago`): mismo selector,
     * mismo timeout, sin mecanismo alternativo. El flujo Paycomet (`jetId`)
     * NO pasa por aquí: emite `continuar-pago` → PASO_PAGO.
     *
     * El flag `enviandoRedsys` se resetea SIEMPRE al dispararse el timeout
     * (submit exitoso o no): si el HTML no contiene `<form>` (backend
     * inconsistente o DOM desmontado por keep-alive), se muestra el error y el
     * botón queda operativo en lugar de bloquearse para siempre.
     */
    function confirmarPagoRedsys() {
        if (enviandoRedsys.value) return;
        cancelarEnvioRedsys();
        errorPagoRedsys.value = false;
        enviandoRedsys.value = true;
        nextTick(() => {
            // Si se canceló mientras esperaba el tick (desactivación del paso),
            // no programar el submit.
            if (!enviandoRedsys.value) return;
            redsysTimer = setTimeout(() => {
                redsysTimer = null;
                enviandoRedsys.value = false;
                const form = document.querySelector('#formulario-compra form');
                if (form) {
                    // El submit navega a la pasarela Redsys y desmonta la SPA:
                    // la liberación de sesión no debe borrar la reserva hasta
                    // que el webhook procese el pago completado.
                    if (props.sessionId) {
                        marcarPagoEnCurso(props.sessionId);
                    }
                    form.submit();
                } else {
                    // Sin <form> no hay pago posible: avisar sin dejar el flag atascado.
                    errorPagoRedsys.value = true;
                }
            }, 500);
        });
    }

    // Limpieza del envío Redsys pendiente: el paso vive dentro de `<keep-alive>`
    // (CambioButacaView), así que al desactivarse se desmonta el DOM pero la
    // instancia queda cacheada. Sin esta limpieza, un timer pendiente dispararía
    // un querySelector sobre un DOM inexistente y `enviandoRedsys` quedaría a
    // true para siempre en la instancia reutilizada.
    onDeactivated(cancelarEnvioRedsys);
    onUnmounted(cancelarEnvioRedsys);

    // ─── Butaca/Plaza actual (del detalle del abono) ─────────────────────

    /**
     * Detecta si la zona actual es de plazas (per-zona) o si el seat seleccionado
     * es una plaza (per-seat, defensa). Se usa para seleccionar los strings
     * i18n `butaca_*` vs `plaza_*` en la UI.
     */
    const esPlaza = computed(() => {
        if (props.zonaSeleccionada?.tiene_plazas === true) return true;
        if (props.asientoSeleccionado?.es_plaza === true) return true;
        return false;
    });

    /**
     * Extrae los datos del asiento actual del abono para la comparativa.
     *
     * Busca en múltiples paths de anidamiento porque el backend puede
     * devolver el asiento como `asiento` o como `plaza`.
     */
    const asientoActual = computed(() => {
        if (!props.abonoDetail) return null;

        // Buscar datos de asiento en distintos paths del detalle
        const src = props.abonoDetail.asiento
            ?? props.abonoDetail.plaza
            ?? props.abonoDetail.abonado?.asiento
            ?? props.abonoDetail.abonado?.plaza
            ?? props.abonoDetail.data?.asiento
            ?? props.abonoDetail.data?.plaza
            ?? null;
        if (!src) return null;

        return {
            // `null` (no '—') cuando no hay región/zona: el template usa
            // `v-if="asientoActual.zona"` y con un string falsy no renderiza
            // la línea (ni el guion).
            zona: (typeof src.region === 'object' ? src.region?.nombre : src.region) ?? src.zona ?? null,
            fila: src.fila ?? src.row ?? '—',
            numero: src.asiento ?? src.seat ?? src.numero ?? src.butaca ?? '—',
            tipoAbono: props.abonoDetail?.precio?.descripcion ?? '—',
        };
    });

    /**
     * Nombre de la zona/región NUEVA seleccionada. El wrapper legacy
     * (`SeleccionarAsientosRegionAnexa`) emite la zona cruda del API; el
     * nombre puede venir como `nombre` (es), `name` (legacy canónico) o
     * `descripcion`. Aceptamos todos. Si no hay zona/región devuelve `null`
     * para que el template (`v-if="zonaNueva"`) no renderice la línea.
     */
    const zonaNueva = computed(() => {
        const z = props.zonaSeleccionada;
        if (!z) return null;
        return z.nombre ?? z.name ?? z.descripcion ?? null;
    });

    // ─── Utilidades ────────────────────────────────────────────────────────

    /** Formatea un número como precio en euros. */
    function formatearPrecio(val) {
        const n = parseFloat(val);
        if (isNaN(n)) return '—';
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
        }).format(n);
    }
</script>
